import {
  useDiscoverPlaybooksQuery,
  useMyAccountQuery,
  usePlaybookSetsQuery,
  useSubscribeToPlaybookSetMutation,
  useUnsubscribeFromPlaybookSetMutation,
} from "@qw/graphql";
import { PlaybookType } from "@qw/qw-common";
import { Column, ColumnContent, Icon, Input, Separator, Spinner } from "@qw/ui";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { PlaybookListItem } from "./playbook-list-item";
import { PlaybookSetCard } from "./playbook-set-card";

export const PlaybooksDiscover = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";

  const { data: meData } = useMyAccountQuery();
  const { data: discoverData, loading: useDiscoverPlaybooksLoading } = useDiscoverPlaybooksQuery();
  const { data: setsData, loading: setsLoading } = usePlaybookSetsQuery();

  const [subscribeToSet] = useSubscribeToPlaybookSetMutation();
  const [unsubscribeFromSet] = useUnsubscribeFromPlaybookSetMutation();

  const handleSearchChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set("search", value);
    } else {
      newSearchParams.delete("search");
    }
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  const filteredPlaybookSets = useMemo(() => {
    // Strip internal/non-browseable playbooks from every set so the tooltip
    // preview and visible count match the standalone discover list.
    const playbookSetsData = (setsData?.playbookSets ?? [])
      .map((set) => {
        const visiblePlaybooks = (set.playbooks ?? []).filter(
          (p) =>
            ((p.type as PlaybookType | undefined) ?? PlaybookType.Workflow) !== PlaybookType.WorkflowStep &&
            ((p.type as PlaybookType | undefined) ?? PlaybookType.Workflow) !== PlaybookType.Template
        );
        return {
          ...set,
          playbooks: visiblePlaybooks,
          playbookCount: visiblePlaybooks.length,
        };
      })
      .filter((set) => set.playbookCount > 0);

    if (!searchQuery.trim()) {
      return playbookSetsData;
    }

    const query = searchQuery.toLowerCase();
    return playbookSetsData.filter((set) => {
      const matchesName = set.name?.toLowerCase().includes(query);
      const matchesDescription = set.description?.toLowerCase().includes(query);

      return matchesName || matchesDescription;
    });
  }, [setsData, searchQuery]);

  const discoverPlaybooks = useMemo(() => {
    const discoverPlaybooksData = discoverData?.discoverPlaybooks ?? [];

    return discoverPlaybooksData
      .filter((p) => {
        // Workflow Step playbooks are internal — invoked from a parent orchestrator
        // by hardcoded UUID — and templates are fill targets, so hide both from
        // discovery browsing.
        if (p.type === PlaybookType.WorkflowStep || p.type === PlaybookType.Template) {
          return false;
        }

        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const matchesName = p.name?.toLowerCase().includes(query);
          const matchesDescription = p.description?.toLowerCase().includes(query);
          const authorName = `${p.author?.firstName ?? ""} ${p.author?.lastName ?? ""}`.trim().toLowerCase() || null;
          const matchesAuthor = authorName?.includes(query);

          if (!matchesName && !matchesDescription && !matchesAuthor) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => (b.subscribers?.length ?? 0) - (a.subscribers?.length ?? 0));
  }, [discoverData, searchQuery]);

  const handleSubscribeToSet = async (setId: string) => {
    await subscribeToSet({
      variables: { id: setId },
      refetchQueries: ["PlaybookSets", "MySubscribedPlaybooks", "MyInvokablePlaybooks"],
    });
  };

  const handleUnsubscribeFromSet = async (setId: string) => {
    await unsubscribeFromSet({
      variables: { id: setId },
      refetchQueries: ["PlaybookSets", "MySubscribedPlaybooks", "MyInvokablePlaybooks"],
    });
  };

  const handleViewSet = (setId: string) => {
    navigate(`/playbooks/discover/${setId}`);
  };

  return (
    <Column className="flex-auto bg-background">
      <ColumnContent className="p-4">
        <label className="relative w-full max-w-sm">
          <Icon icon="search" className="text-base absolute left-0 top-3.25 pointer-events-none w-9" />
          <span className="sr-only">Search playbooks by name, description, or author</span>
          <Input
            placeholder="Search playbooks by name, description, or author"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 py-2 rounded-full bg-muted border-0"
          />
        </label>

        {setsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : (
          <>
            {filteredPlaybookSets.length > 0 && (
              <>
                <Separator className="my-8" />
                <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
                  {filteredPlaybookSets.map((set) => (
                    <PlaybookSetCard
                      key={set.id}
                      id={set.id}
                      name={set.name}
                      playbookCount={set.playbookCount}
                      isSubscribed={set.isSubscribed}
                      playbooks={set.playbooks}
                      onSubscribe={handleSubscribeToSet}
                      onUnsubscribe={handleUnsubscribeFromSet}
                      onViewSet={handleViewSet}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        <Separator className="my-8" />

        {useDiscoverPlaybooksLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : (
          <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
            {discoverPlaybooks.map((p) => (
              <PlaybookListItem
                key={p.id}
                title={p.name || "(untitled)"}
                description={p.description ?? undefined}
                author={`${p.author?.firstName ?? ""} ${p.author?.lastName ?? ""}`.trim()}
                type={(p.type as PlaybookType | undefined) ?? PlaybookType.Workflow}
                subscribers={p.subscribers ?? []}
                subscribed={p.subscribers?.some((s) => s.id === meData?.myAccount?.id)}
                onClick={() => navigate(`/playbooks/${p.id}`, { state: { fromDiscover: true } })}
              />
            ))}
          </div>
        )}
      </ColumnContent>
    </Column>
  );
};
